/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { entityColors } from 'src/core/themes/themeBuilder';
import { Label } from 'src/modules/labeling/types/Label';
import { AnnotationData } from 'src/packages/react-text-annotator/lib/esm';

/**
 * Helper function that returns the label's start and end token indices.
 *
 * @param param0.tokenizedText the tokenized text
 * @param param0.startCharIndex the start char index to convert.
 * @param param0.endCharIndex the end token index to convert.
 */
export const charToTokenIndices = ({
    tokenizedText,
    startCharIndex,
    endCharIndex
}: {
    tokenizedText: string[];
    startCharIndex: number;
    endCharIndex: number;
}) => {
    let startLength: number = 0;
    let endLength: number = -2;
    let startTokenIndex: number;
    let endTokenIndex: number;

    for (let i: number = 0; i < tokenizedText.length; i += 1) {
        if (startCharIndex - startLength === 0) {
            startTokenIndex = i;
            break;
        } else {
            startLength += tokenizedText[i].length + 1;
        }
    }

    for (let i: number = 0; i < tokenizedText.length; i += 1) {
        endLength += tokenizedText[i].length + 1;

        if (endCharIndex - endLength === 0) {
            endTokenIndex = i;
            break;
        }
    }

    return { startTokenIndex, endTokenIndex };
};

/**
 * Returns entity character indices converted from token indices. Requires original
 * text and tokenized text for calculations.
 *
 * @param param0.text The original text the entity was labeled in.
 * @param param0.tokenizedText The original tokenized text.
 * @param param0.startTokenIndex The start token index to convert.
 * @param param0.endTokenIndex The end token index to convert.
 * @param param0.isCharacterTokenization Whether the tokens are actually
 * character tokenized or not. This differs because if we are using
 * character tokenization, white spaces are counted as tokens as well,
 * while in token tokenization mode, white spaces are separators.
 */
export const tokenToCharIndices = ({
    text,
    tokenizedText,
    startTokenIndex,
    endTokenIndex,
    isCharacterTokenization = false
}: {
    text: string;
    tokenizedText: string[];
    startTokenIndex: number;
    endTokenIndex: number;
    isCharacterTokenization?: boolean;
}) => {
    let startCharIndex: number;
    let endCharIndex: number;
    const tokenCharacterMap: Map<number, number> = new Map<number, number>();

    tokenizedText.reduce((lastIndex, t, i) => {
        const whiteSpaceOffset = isCharacterTokenization ? 0 : text[lastIndex + t.length] === ' ' ? 1 : 0;
        tokenCharacterMap.set(i, text.indexOf(t, lastIndex));

        return lastIndex + t.length + whiteSpaceOffset;
    }, 0);

    if (tokenCharacterMap.get(startTokenIndex) != null && tokenCharacterMap.get(endTokenIndex) != null) {
        startCharIndex = tokenCharacterMap.get(startTokenIndex);
        endCharIndex = tokenCharacterMap.get(endTokenIndex) + tokenizedText[endTokenIndex].length - 1;
    }

    return { startCharIndex, endCharIndex };
};

/**
 * Helper function that converts label to annotation.
 *
 * @param param0.label the label to convert.
 * @param param0.tooltipContent the content element of the labeler's tooltip.
 * @param param0.resolveShapeColor the function that returns annotation color given a label.
 * @returns AnnotationData represents the given label.
 */

export const transformLabelToAnnotation = (
    label: Label,
    tooltipContent: JSX.Element,
    resolveShapeColor?: (label: Label) => string
): AnnotationData => ({
    endToken: label.end,
    startToken: label.start,
    name: label.entity.name,
    level: label.renderingLevel - 1,
    onRenderTooltip: tooltipContent ? () => tooltipContent : undefined,
    color: resolveShapeColor ? resolveShapeColor(label) : entityColors.defaultColor,
    id: `${label.entity.name}-${label.renderedAs}-${label.renderingLevel}-${label.start}-${label.end}`,
    kind: label.renderedAs === 'box' ? 'prediction' : label.viewOptions?.isDashed ? 'negativeLabel' : 'label'
});
